import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Zap,
  Twitter,
  ImageDown,
  Copy,
  RefreshCw,
  Terminal,
  Flame,
  History,
  Briefcase,
  Rocket,
  Music,
  Shirt,
  Heart,
  Bitcoin,
  Camera,
  Hash,
  Upload,
  X,
  Link as LinkIcon,
  Type,
  Code2,
  ChefHat,
  Store,
  ChevronLeft,
  ChevronRight,
  Download,
  Lock,
} from "lucide-react";

import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useTypewriter from "@/hooks/useTypewriter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLES = [
  {
    label: "📸 INSTAGRAM BIO",
    category: "instagram",
    kind: "image",
    icon: Camera,
    text: "Roast my Instagram profile and bio. Don't hold back on my aesthetic choices.",
  },
  {
    label: "🐦 X (TWITTER) FEED",
    category: "twitter_profile",
    kind: "image",
    icon: Twitter,
    text: "Exterminate my X (Twitter) persona. Analyze my tweets and my overall vibe.",
  },
  {
    label: "🎵 TIKTOK VIBE",
    category: "tiktok",
    kind: "image",
    icon: Music,
    text: "Roast my TikTok profile. Is it cringe or just desperate?",
  },
  {
    label: "LinkedIn Headline",
    category: "linkedin",
    kind: "text",
    icon: Briefcase,
    text: "Roast my LinkedIn headline: 'Visionary thought leader. Disruptor. Synergy evangelist.'",
  },
  {
    label: "Startup Idea",
    category: "startup",
    kind: "text",
    icon: Rocket,
    text: "Roast my startup idea: Uber, but for pet rocks.",
  },
  {
    label: "Spotify Taste",
    category: "spotify",
    kind: "text",
    icon: Music,
    text: "Roast my Spotify taste: Taylor Swift, lo-fi beats, and 2008 emo.",
  },
  {
    label: "Fashion Sense",
    category: "fashion",
    kind: "text",
    icon: Shirt,
    text: "Roast my fashion sense: cargo shorts, crocs, and a tie.",
  },
  {
    label: "Dating App Bio",
    category: "dating_bio",
    kind: "text",
    icon: Heart,
    text: "Roast my dating app bio: '6'2\", fluent in sarcasm, love to travel, dog dad, just here for vibes. Swipe right if you can handle me at my best.'",
  },
  {
    label: "Crypto/NFT Portfolio",
    category: "crypto",
    kind: "text",
    icon: Bitcoin,
    text: "Roast my crypto/NFT portfolio: bought Dogecoin at the top, 3 ape JPEGs, some Luna before the crash, and I'm still saying 'we're so back' on every green candle.",
  },
  {
    label: "Tinder/Hinge Photos",
    category: "photo",
    kind: "image",
    icon: Camera,
    text: "",
  },
  {
    label: "Gamertags/Nicks",
    category: "gamertag",
    kind: "text",
    icon: Hash,
    text: "Roast my gamertag: xXx_DarkShadowLord69_xXx. Been using it since 2007.",
  },
  {
    label: "Spaghetti Code / GitHub",
    category: "spaghetti_code",
    kind: "text",
    icon: Code2,
    text: "Roast my code: function doStuff(data, x, thing) { var temp = data; for (var i=0; i<temp.length; i++) { if (temp[i].x == true) { /* TODO: fix this */ console.log('works!'); } } } // No tests. Repo README just says 'TODO'.",
  },
  {
    label: "Sad Home Cooking",
    category: "cooking",
    kind: "image",
    icon: ChefHat,
    text: "",
  },
  {
    label: "Side Hustle / Store",
    category: "side_hustle",
    kind: "text",
    icon: Store,
    text: "Roast my side hustle: a Shopify dropshipping store selling minimalist resin coasters called 'LUXE.' Logo is black Helvetica on white. TikTok-only marketing. 'Coming Soon' landing page for 4 months.",
  },
];

const LOADING_PHASES = [
  "INITIATING DIAGNOSTIC",
  "SCANNING EGO MATRIX",
  "PARSING DELUSIONS",
  "CROSS-REFERENCING BANTER DB",
  "COMPILING EXECUTION",
];

export default function SynthRoast() {
  const [prompt, setPrompt] = useState("");
  const [intensity, setIntensity] = useState("savage");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [roast, setRoast] = useState("");
  const [history, setHistory] = useState([]);
  const textExamples = EXAMPLES.filter((e) => e.kind === "text");
  const [placeholder, setPlaceholder] = useState(textExamples[0]?.text || "");
  // input mode: "text" | "image"
  const [inputMode, setInputMode] = useState("text");
  const [category, setCategory] = useState("generic");
  const [activeChip, setActiveChip] = useState(null);
  // image state
  const [imageDataUrl, setImageDataUrl] = useState(""); // full data URL for preview
  const [imageBase64, setImageBase64] = useState(""); // stripped base64 for API
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [photoHint, setPhotoHint] = useState("");
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);

  // rotate placeholder (only among text categories)
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % textExamples.length;
      setPlaceholder(textExamples[i].text);
    }, 3500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fake progress animation
  useEffect(() => {
    if (!loading) {
      setProgress(0);
      setPhaseIdx(0);
      return;
    }
    const id = setInterval(() => {
      setProgress((p) => {
        const next = p + Math.max(1, Math.round((95 - p) * 0.08));
        return Math.min(next, 95);
      });
    }, 180);
    const phaseId = setInterval(() => {
      setPhaseIdx((i) => (i + 1) % LOADING_PHASES.length);
    }, 900);
    return () => {
      clearInterval(id);
      clearInterval(phaseId);
    };
  }, [loading]);

  const pickExample = (ex, idx) => {
    setActiveChip(idx);
    setCategory(ex.category);
    setRoast("");
    if (ex.kind === "image") {
      setInputMode("image");
      setPrompt("");
    } else {
      setInputMode("text");
      setPrompt(ex.text);
      setImageDataUrl("");
      setImageBase64("");
      setImageUrlInput("");
    }
  };

  const clearImage = () => {
    setImageDataUrl("");
    setImageBase64("");
    setImageUrlInput("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("JPEG, PNG, or WEBP only.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large. Keep it under 8MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setImageDataUrl(dataUrl);
      const b64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      setImageBase64(b64);
    };
    reader.onerror = () => toast.error("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const onFileChange = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const loadImageFromUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url) {
      toast.error("Paste an image URL first.");
      return;
    }
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      if (!["image/jpeg", "image/png", "image/webp"].includes(blob.type)) {
        toast.error("URL must point to JPEG/PNG/WEBP.");
        return;
      }
      const file = new File([blob], "from-url", { type: blob.type });
      handleFile(file);
    } catch (_) {
      toast.error("Couldn't fetch that URL (CORS or invalid image).");
    }
  };

  const submitRoast = async () => {
    if (loading) return;

    if (inputMode === "image") {
      if (!imageBase64) {
        toast.error("Upload a photo first.");
        return;
      }
      // Image category is whichever image chip is active; default to "photo"
      const imgCategory = category === "cooking" ? "cooking" : "photo";
      setRoast("");
      setLoading(true);
      try {
        const { data } = await axios.post("https://www.synthroast.com/api/roast/image", {
          image_base64: imageBase64,
          intensity,
          category: imgCategory,
          hint: photoHint.trim() || undefined,
        });
        setProgress(100);
        const labelPrefix = imgCategory === "cooking" ? "[Sad Home Cooking]" : "[Dating App Photo]";
        setTimeout(() => {
          setRoast(data.roast);
          setHistory((h) =>
            [
              {
                prompt: labelPrefix + (photoHint ? ` · ${photoHint}` : ""),
                intensity,
                roast: data.roast,
                id: data.id,
                category: imgCategory,
              },
              ...h,
            ].slice(0, 5)
          );
        }, 250);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.detail || "Vision engine offline.");
      } finally {
        setTimeout(() => setLoading(false), 400);
      }
      return;
    }

    const text = prompt.trim();
    if (!text) {
      toast.error("Feed the machine something to destroy.");
      return;
    }
    setRoast("");
    setLoading(true);
    try {
      const { data } = await axios.post("https://www.synthroast.com/api/roast", {
        prompt: text,
        intensity,
        category,
      });
      setProgress(100);
      setTimeout(() => {
        setRoast(data.roast);
        setHistory((h) =>
          [{ prompt: text, intensity, roast: data.roast, id: data.id, category }, ...h].slice(0, 5)
        );
      }, 250);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Roast engine offline. Try again.");
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  };

  const shareToX = () => {
    if (!roast) return;
    const clipped = roast.length > 230 ? roast.slice(0, 227) + "..." : roast;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `I was roasted by SynthRoast:\n\n"${clipped}"\n\n`
    )}&hashtags=SynthRoast`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyAsImage = async () => {
    if (!resultRef.current) return;
    try {
      const dataUrl = await toPng(resultRef.current, {
        backgroundColor: "#050505",
        pixelRatio: 2,
        cacheBust: true,
        filter: (node) => {
          return !(node.dataset && node.dataset.excludeFromCapture === "true");
        },
      });
      // try writing to clipboard as image
      try {
        const blob = await (await fetch(dataUrl)).blob();
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ [blob.type]: blob }),
          ]);
          toast.success("Roast card copied to clipboard.");
          return;
        }
      } catch (_) {
        // fall through to download
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "synthroast.png";
      a.click();
      toast.success("Image downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Image capture failed.");
    }
  };

  const downloadReceipt = async () => {
    if (!resultRef.current || downloading) return;
    setDownloading(true);
    // Let React render the watermark + overlay before capturing
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    // Tiny extra delay to make the "ENCRYPTING IMAGE" state feel real
    await new Promise((r) => setTimeout(r, 650));
    try {
      const dataUrl = await toPng(resultRef.current, {
        backgroundColor: "#050505",
        pixelRatio: 2.25,
        cacheBust: true,
        skipFonts: false,
        // Exclude the overlay from the captured image, but keep the watermark
        filter: (node) => {
          return !(node.dataset && node.dataset.excludeFromCapture === "true");
        },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `synthroast-receipt-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Receipt saved. Share the damage.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't encrypt image. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const copyText = async () => {
    if (!roast) return;
    try {
      await navigator.clipboard.writeText(roast);
      toast.success("Roast copied.");
    } catch (_) {
      toast.error("Clipboard blocked.");
    }
  };

  return (
    <div className="min-h-screen w-full relative text-white" data-testid="synthroast-page">
      {/* ambient grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(157,76,221,0.4) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 75%)",
        }}
      />

      {/* Nav */}
      <header className="relative z-10 border-b border-zinc-900/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand">
            <div className="w-2 h-2 bg-cyber-cyan shadow-[0_0_12px_#00FFFF]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
              /terminal · v2.099
            </span>
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <span className="hidden sm:inline">STATUS:</span>
            <span className="text-cyber-cyan flicker">ONLINE</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-24">
        {/* Hero */}
        <section className="mb-10 sm:mb-16">
          <div className="text-[10px] uppercase tracking-[0.4em] text-cyber-purple mb-4 flex items-center gap-2">
            <Terminal className="w-3 h-3" strokeWidth={1.5} />
            <span>protocol // extermination</span>
          </div>
          <h1
            data-testid="header-title"
            data-text="SYNTHROAST"
            className="glitch font-display font-black uppercase text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9]"
          >
            SYNTHROAST
          </h1>
          <p className="mt-6 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
            Submit yourself to the insult comedian from 2099. Your LinkedIn, your startup,
            your fashion sense — all equally deserving. Expect{" "}
            <span className="text-cyber-cyan">surgical</span> and{" "}
            <span className="text-cyber-purple">unkind</span> results.
          </p>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Input column */}
          <div className="md:col-span-8 space-y-6">
            {/* Input card */}
            <div className="border border-zinc-800 bg-[#0A0A0A] p-5 sm:p-6 relative" data-testid="input-card">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
                  &gt; input_buffer
                  <span className="inline-flex items-center gap-1 border border-zinc-800 px-1.5 py-0.5 text-zinc-400">
                    {inputMode === "image" ? (
                      <>
                        <Camera className="w-3 h-3 text-cyber-purple" strokeWidth={1.5} />
                        <span className="text-cyber-purple">photo_mode</span>
                      </>
                    ) : (
                      <>
                        <Type className="w-3 h-3 text-cyber-cyan" strokeWidth={1.5} />
                        <span className="text-cyber-cyan">text_mode</span>
                      </>
                    )}
                  </span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500" data-testid="input-counter">
                  {inputMode === "image"
                    ? imageBase64
                      ? "IMAGE LOADED"
                      : "AWAITING IMAGE"
                    : `${prompt.length}/1000`}
                </span>
              </div>

              {inputMode === "text" && (
  <>
    <Textarea
      data-testid="roast-input"
      value={prompt}
      onChange={(e) => {
        setPrompt(e.target.value.slice(0, 1000));
        setCategory((c) =>
          c === "photo" || c === "cooking" ? "generic" : c
        );
      }}
      placeholder={placeholder}
      className="min-h-[160px] resize-none bg-transparent border-0 border-b-2 border-zinc-800 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-cyber-cyan text-base sm:text-lg font-mono px-0 text-white placeholder:text-zinc-600 rounded-none"
    />
    <div className="mt-4 flex items-center justify-start">
      <button
        onClick={() => setInputMode("image")}
        className="group flex items-center gap-2 border border-cyber-purple/30 bg-cyber-purple/5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-cyber-purple hover:bg-cyber-purple hover:text-white transition-all duration-300"
      >
        <Camera className="w-3.5 h-3.5" />
        <span>Add Profile Screenshot for Deeper Roast</span>
      </button>
    </div>
  </>
)}

              {inputMode === "image" && (
                <div className="space-y-4" data-testid="image-upload-area">
                  {!imageDataUrl ? (
                    <div
                      onDrop={onDrop}
                      onDragOver={(e) => e.preventDefault()}
                      className="border-2 border-dashed border-zinc-700 hover:border-cyber-purple/70 transition-colors p-8 text-center cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="image-dropzone"
                    >
                      <Upload className="w-8 h-8 mx-auto text-cyber-purple mb-3" strokeWidth={1.5} />
                      <div className="text-sm text-white mb-1 font-display uppercase tracking-widest">
                        {category === "cooking" ? "Drop culinary crime here" : "Drop photo here"}
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        JPEG · PNG · WEBP · max 8MB
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={onFileChange}
                        data-testid="file-input"
                      />
                    </div>
                  ) : (
                    <div className="relative border border-cyber-purple/60 p-3 bg-black">
                      <img
                        src={imageDataUrl}
                        alt="upload preview"
                        className="max-h-[320px] w-full object-contain"
                        data-testid="image-preview"
                      />
                      <button
                        onClick={clearImage}
                        data-testid="clear-image-button"
                        className="absolute top-2 right-2 border border-zinc-700 bg-black/70 p-1.5 text-zinc-300 hover:text-white hover:border-white"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 flex items-center gap-2 border border-zinc-800 px-3 py-2">
                      <LinkIcon className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
                      <input
                        type="url"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        placeholder="or paste image URL..."
                        className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none font-mono"
                        data-testid="image-url-input"
                      />
                    </div>
                    <button
                      onClick={loadImageFromUrl}
                      data-testid="load-url-button"
                      className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-widest text-zinc-300 hover:text-cyber-cyan hover:border-cyber-cyan/60 transition-colors"
                    >
                      Load URL
                    </button>
                  </div>

                  <input
                    type="text"
                    value={photoHint}
                    onChange={(e) => setPhotoHint(e.target.value.slice(0, 200))}
                    placeholder={
                      category === "cooking"
                        ? "Optional hint (e.g. 'spaghetti carbonara I made tonight')"
                        : "Optional hint (e.g. 'this is my Hinge main photo')"
                    }
                    data-testid="photo-hint-input"
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-cyber-cyan focus:outline-none py-2 text-sm font-mono text-white placeholder:text-zinc-600"
                  />
                </div>
              )}

              {/* Example chips — 4x2 desktop / 2x4 mobile */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    &gt; suggested_targets · {EXAMPLES.length}
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                    <ChevronLeft className="w-3 h-3" strokeWidth={1.5} />
                    <span>scroll</span>
                    <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
                  </div>
                </div>
                <div
                  className="relative -mx-5 sm:-mx-6 px-5 sm:px-6"
                  data-testid="chip-rail-wrapper"
                >
                  {/* left fade */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10"
                  />
                  {/* right fade */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#0A0A0A] to-transparent z-10"
                  />
                  <div
                    className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-thin"
                    data-testid="chip-rail"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    {EXAMPLES.map((ex, i) => {
                      const Icon = ex.icon;
                      const active = activeChip === i;
                      const accent = ex.kind === "image" ? "purple" : "cyan";
                      return (
                        <button
                          key={i}
                          data-testid={`example-chip-${i}`}
                          data-category={ex.category}
                          data-kind={ex.kind}
                          onClick={() => pickExample(ex, i)}
                          className={`group snap-start shrink-0 flex items-center gap-2 text-[11px] uppercase tracking-widest border px-4 py-2.5 transition-all duration-200 ${
                            active
                              ? accent === "purple"
                                ? "border-cyber-purple text-cyber-purple bg-cyber-purple/10 shadow-[0_0_12px_rgba(157,76,221,0.35)]"
                                : "border-cyber-cyan text-cyber-cyan bg-cyber-cyan/5 shadow-[0_0_12px_rgba(0,255,255,0.35)]"
                              : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 hover:bg-white/[0.02]"
                          }`}
                        >
                          <Icon
                            className={`w-3.5 h-3.5 shrink-0 ${
                              active
                                ? accent === "purple"
                                  ? "text-cyber-purple"
                                  : "text-cyber-cyan"
                                : "text-zinc-500 group-hover:text-white"
                            }`}
                            strokeWidth={1.5}
                          />
                          <span className="whitespace-nowrap">{ex.label}</span>
                          {ex.kind === "image" && (
                            <span
                              className={`ml-1 text-[9px] tracking-[0.2em] border px-1 py-0.5 ${
                                active
                                  ? "border-cyber-purple/70 text-cyber-purple"
                                  : "border-zinc-700 text-zinc-500 group-hover:text-white group-hover:border-zinc-500"
                              }`}
                            >
                              IMG
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Intensity + submit */}
            <div className="border border-zinc-800 bg-[#0A0A0A] p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                    roast_intensity
                  </div>
                  <div className="text-sm text-zinc-300 flex items-center gap-2">
                    <Flame className="w-4 h-4 text-cyber-purple" strokeWidth={1.5} />
                    <span>Calibrate the brutality of your extermination.</span>
                  </div>
                </div>
                <Tabs
                  value={intensity}
                  onValueChange={setIntensity}
                  data-testid="intensity-selector"
                >
                  <TabsList className="bg-transparent border border-zinc-800 p-1 rounded-none">
                    <TabsTrigger
                      value="mild"
                      data-testid="intensity-mild"
                      className="rounded-none data-[state=active]:bg-cyber-cyan/10 data-[state=active]:text-cyber-cyan data-[state=active]:shadow-none text-xs uppercase tracking-widest px-4"
                    >
                      Mild
                    </TabsTrigger>
                    <TabsTrigger
                      value="medium"
                      data-testid="intensity-medium"
                      className="rounded-none data-[state=active]:bg-cyber-purple/20 data-[state=active]:text-cyber-purple data-[state=active]:shadow-none text-xs uppercase tracking-widest px-4"
                    >
                      Medium
                    </TabsTrigger>
                    <TabsTrigger
                      value="savage"
                      data-testid="intensity-savage"
                      className="rounded-none data-[state=active]:bg-red-500/10 data-[state=active]:text-red-400 data-[state=active]:shadow-none text-xs uppercase tracking-widest px-4"
                    >
                      Savage
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <button
                data-testid="roast-submit-button"
                onClick={() => submitRoast()}
                disabled={loading}
                className="group w-full border border-cyber-cyan bg-cyber-cyan/5 text-cyber-cyan font-display font-bold uppercase tracking-[0.3em] text-sm sm:text-base py-5 sm:py-6 hover:bg-cyber-cyan hover:text-black hover:shadow-[0_0_40px_rgba(0,255,255,0.55)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                <Zap className="w-5 h-5" strokeWidth={2} />
                <span>{loading ? "Exterminating..." : "Exterminate / Roast Me"}</span>
              </button>
            </div>

            {/* Loading panel */}
            {loading && (
              <div
                data-testid="loading-indicator"
                className="border border-cyber-cyan/40 bg-[#050505] p-5 sm:p-6 relative overflow-hidden rise-in noise"
              >
                <div className="scan-line" />
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-cyber-cyan">
                    {LOADING_PHASES[phaseIdx]}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {Math.floor(progress)}%
                  </span>
                </div>
                <div className="flex gap-1 h-3">
                  {Array.from({ length: 24 }).map((_, i) => {
                    const filled = (i + 1) / 24 <= progress / 100;
                    return (
                      <div
                        key={i}
                        className={`flex-1 ${
                          filled ? "seg" : "bg-zinc-900 border border-zinc-800/50"
                        }`}
                      />
                    );
                  })}
                </div>
                <div className="mt-4 font-mono text-[11px] text-zinc-500 grid grid-cols-2 gap-y-1">
                  <span>&gt; target.locked = true</span>
                  <span>&gt; witticism.engine = armed</span>
                  <span>&gt; empathy.module = disabled</span>
                  <span>&gt; mic.drop = pending</span>
                </div>
              </div>
            )}

            {/* Result */}
            {roast && !loading && (
              <ResultCard
                roast={roast}
                intensity={intensity}
                innerRef={resultRef}
                downloading={downloading}
                onShareX={shareToX}
                onCopyImage={copyAsImage}
                onCopyText={copyText}
                onRoastAgain={() => submitRoast()}
                onDownload={downloadReceipt}
              />
            )}
          </div>

          {/* Sidebar */}
          <aside className="md:col-span-4 space-y-6">
            <div className="border border-zinc-800 bg-[#0A0A0A] p-5">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-4 h-4 text-cyber-purple" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  session_history
                </span>
              </div>
              {history.length === 0 ? (
                <p className="text-xs text-zinc-600 leading-relaxed">
                  No victims yet. Your ego is still intact.
                </p>
              ) : (
                <ul className="space-y-3" data-testid="history-list">
                  {history.map((h, i) => (
                    <li
                      key={h.id}
                      className="border border-zinc-900 p-3 hover:border-cyber-cyan/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setIntensity(h.intensity);
                        setRoast(h.roast);
                        if (h.category === "photo" || h.category === "cooking") {
                          setInputMode("image");
                          setCategory(h.category);
                          setPrompt("");
                        } else {
                          setInputMode("text");
                          setCategory(h.category || "generic");
                          setPrompt(h.prompt);
                        }
                      }}
                      data-testid={`history-item-${i}`}
                    >
                      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 mb-1.5 flex items-center justify-between">
                        <span>{h.intensity} · #{String(i + 1).padStart(2, "0")}</span>
                        {h.category && h.category !== "generic" && (
                          <span className="text-cyber-purple">{h.category}</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-300 line-clamp-2">
                        "{h.prompt}"
                      </div>
                      <div className="mt-2 text-[11px] text-zinc-500 line-clamp-3">
                        {h.roast}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border border-zinc-800 bg-[#0A0A0A] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                // manifest
              </div>
              <ul className="text-xs text-zinc-400 space-y-2 leading-relaxed">
                <li>&gt; Powered by Claude Sonnet 4.5 (vision)</li>
                <li>&gt; 11 roast categories · 2 photo modes</li>
                <li>&gt; High-level banter only</li>
                <li>&gt; No hate speech, ever</li>
              </ul>
            </div>
          </aside>
        </div>
      </main>

      <footer className="relative z-10 border-t border-zinc-900/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          <span>synthroast © 2099 · all egos reserved</span>
          <span>/ built in the void</span>
        </div>
      </footer>
    </div>
  );
}

function ResultCard({ roast, intensity, innerRef, downloading, onShareX, onCopyImage, onCopyText, onRoastAgain, onDownload }) {
  const { output, done } = useTypewriter(roast, 14);
  const intensityColor =
    intensity === "savage"
      ? "text-red-400 border-red-500/50"
      : intensity === "medium"
      ? "text-cyber-purple border-cyber-purple/50"
      : "text-cyber-cyan border-cyber-cyan/50";

  return (
    <div className="rise-in" data-testid="roast-result-wrapper">
      <div
        ref={innerRef}
        data-testid="roast-result"
        className={`relative border bg-[#050505] p-6 sm:p-8 noise ${intensityColor}`}
      >
        <div className="flex items-center justify-between mb-4">
          <span className={`text-[10px] uppercase tracking-[0.3em] ${intensityColor}`}>
            // verdict · {intensity}
          </span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            synthroast.exe
          </span>
        </div>
        <div className="text-lg sm:text-xl leading-[1.55] text-white whitespace-pre-wrap font-mono">
          {output}
          {!done && <span className="caret" />}
        </div>
        <div className="mt-6 border-t border-zinc-900 pt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          <span>generated by synthroast</span>
          <span>#synthroast</span>
        </div>

        {/* Watermark — rendered only during download, and INCLUDED in the captured PNG */}
        {downloading && (
          <div
            data-testid="receipt-watermark"
            className="mt-5 pt-4 border-t border-dashed border-cyber-cyan/40 flex items-center justify-between"
            style={{ fontFamily: '"Unbounded", sans-serif' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-cyber-cyan shadow-[0_0_10px_#00FFFF]" />
              <span className="text-[11px] uppercase tracking-[0.32em] font-bold text-white">
                Roasted by{" "}
                <span className="text-cyber-cyan" style={{ textShadow: "0 0 10px rgba(0,255,255,0.6)" }}>
                  SYNTHROAST
                </span>
              </span>
            </div>
            <span
              className="text-[10px] uppercase tracking-[0.28em] text-cyber-purple"
              style={{ textShadow: "0 0 8px rgba(157,76,221,0.55)" }}
            >
              synthroast.com
            </span>
          </div>
        )}

        {/* Encrypting overlay — EXCLUDED from capture via data-exclude-from-capture */}
        {downloading && (
          <div
            data-exclude-from-capture="true"
            data-testid="encrypting-overlay"
            className="absolute inset-0 z-30 bg-black/75 backdrop-blur-[2px] flex flex-col items-center justify-center overflow-hidden"
          >
            <div data-exclude-from-capture="true" className="scan-line" />
            <Lock
              className="w-8 h-8 text-cyber-cyan mb-4 flicker"
              strokeWidth={1.5}
              data-exclude-from-capture="true"
            />
            <div
              data-exclude-from-capture="true"
              className="font-display uppercase tracking-[0.4em] text-cyber-cyan text-sm sm:text-base"
              style={{ textShadow: "0 0 12px rgba(0,255,255,0.7)" }}
            >
              Encrypting Image...
            </div>
            <div
              data-exclude-from-capture="true"
              className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono"
            >
              &gt; rendering cyberpunk receipt · pixelRatio 2.25x
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <ActionBtn onClick={onShareX} testId="share-x-button" icon={Twitter} label="Share to X" />
        <ActionBtn
          onClick={onDownload}
          testId="download-receipt-button"
          icon={Download}
          label={downloading ? "Encrypting..." : "Download"}
          disabled={downloading}
          accent="cyan"
        />
        <ActionBtn onClick={onCopyImage} testId="copy-image-button" icon={ImageDown} label="Copy Image" />
        <ActionBtn onClick={onCopyText} testId="copy-text-button" icon={Copy} label="Copy Text" />
        <ActionBtn
          onClick={onRoastAgain}
          testId="roast-again-button"
          icon={RefreshCw}
          label="Roast Again"
        />
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testId, disabled, accent }) {
  const accentCls =
    accent === "cyan"
      ? "border-cyber-cyan/60 text-cyber-cyan hover:bg-cyber-cyan hover:text-black hover:shadow-[0_0_18px_rgba(0,255,255,0.55)]"
      : "border-zinc-800 text-zinc-400 hover:text-white hover:border-white";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex items-center gap-2 border bg-transparent px-3 py-2.5 text-xs uppercase tracking-widest transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${accentCls}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span>{label}</span>
    </button>
  );
}
